import { zodResolver } from "@hookform/resolvers/zod";
import { useOplogg } from "features/TodoOploggSidebar/Oplogg/useOplogg";
import { InputTextarea } from "features/TodoOploggSidebar/common/InputTextarea";
import { ShowIfTrue } from "features/TodoOploggSidebar/common/ShowIfTrue";
import { getDefaultReceivingRoles } from "features/TodoOploggSidebar/common/utils";
import {
  commonPadding,
  OploggForm,
  oploggForm,
  VEHICLE_SET_TYPES,
} from "features/TodoOploggSidebar/formSchema";
import { useState } from "react";
import { FormProvider } from "react-hook-form";
import { useTranslation } from "react-i18next";
import {
  Checkbox,
  CheckboxGroup,
  ChoiceChip,
  DropsButton,
  DropsExpandable,
  Field,
  Grid,
  HStack,
  Stack,
  Text,
} from "shared/components/Spor";
import { useFormVerbose } from "shared/form/useFormVerbose";
import { useUserInfo } from "shared/hooks/useUserInfo";
import { TODO_ROLES, UserRole } from "shared/types/roles";
import { toggle } from "shared/utils/objectUtils";
import { useDropsRole } from "stores/useDropsRole";
import styled, { useTheme } from "styled-components";

const FormContainer = styled.div`
  position: relative;
  display: relative;
`;

const LabelContainer = styled.label`
  display: flex;
  align-items: center;
  cursor: pointer;
  padding-bottom: 1rem;
  padding-top: 1rem;
`;

const ExpandableSection = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  margin: 0 auto;
  padding-bottom: 12px;
`;

interface ExpandableWrapperProps {
  marginLeft?: boolean;
}

const ExpandableWrapper = styled.div<ExpandableWrapperProps>`
  flex: 1;
  margin-left: ${(props) => (props.marginLeft ? "10px" : "0")};
`;

interface OploggSendMessageProps {
  setMessaging: (value: boolean) => void;
}

export const OploggSendMessage = ({ setMessaging }: OploggSendMessageProps) => {
  const { role: currentUserRole } = useDropsRole();
  const { post, postStatus, postToDo } = useOplogg();
  const [sendToTodo, setSendToTodo] = useState(false);
  const [roles, setRoles] = useState<UserRole[]>(
    getDefaultReceivingRoles(currentUserRole),
  );
  const [vehicleSetTypes, setVehicleSetTypes] = useState<string[]>([]);
  const { displayName } = useUserInfo();

  const toggleVehicleSetType = (value: any) => {
    setVehicleSetTypes((prevTypes) =>
      prevTypes.includes(value)
        ? prevTypes.filter((type) => type !== value)
        : [...prevTypes, value],
    );
  };

  const formMethods = useFormVerbose<OploggForm>({
    resolver: zodResolver(oploggForm),
    defaultValues: {
      message: "",
      author:
        currentUserRole === UserRole.oks ? `${displayName} (OKS)` : displayName,
    },
  });

  const {
    reset,
    handleSubmit,
    formState: { errors },
  } = formMethods;
  const { t } = useTranslation();

  const onSubmit = handleSubmit((data) => {
    post(data);

    if (sendToTodo) {
      const todoData = {
        description: data.message,
        roles: roles || [],
        vehicleSetTypes: [vehicleSetTypes.join(",")], // this formatting works for the backend, should it be this way?
        username: data.author,
      };
      postToDo(todoData);
      setTimeout(() => {
        setMessaging(false);
      }, 500);
    }
    reset();
  });

  const theme = useTheme();

  return (
    <Grid
      gridArea="todooplogg"
      gap="12px"
      mt="6px"
      mb="2px"
      padding={commonPadding}
      backgroundColor={theme.colorBackgroundMain}
      borderColor={theme.colorOutlineSecondary}
      borderWidth="1px"
      borderRadius="6px"
    >
      <FormProvider {...formMethods}>
        <form onSubmit={onSubmit}>
          <Field>
            <Text fontWeight="bold" paddingBottom="6px">
              {t("Ny melding")}
            </Text>
            <InputTextarea label="Skriv inn tekst her..." formField="message" />
          </Field>
          <FormContainer>
            <LabelContainer>
              <Checkbox
                checked={sendToTodo}
                onCheckedChange={() => setSendToTodo(!sendToTodo)}
              />
              <Text variant="xs" pl="5px">
                Send også til Todo
              </Text>
            </LabelContainer>

            <ShowIfTrue show={sendToTodo}>
              <ExpandableSection>
                <ExpandableWrapper>
                  <DropsExpandable collapsible title="Send til">
                    <Stack>
                      <CheckboxGroup direction="column" value={roles}>
                        {TODO_ROLES.map((role) => (
                          <Checkbox
                            key={role.value}
                            name="role"
                            value={role.value}
                            onCheckedChange={() =>
                              setRoles(toggle(roles, role.value))
                            }
                          >
                            {role.abbreviation?.toLocaleUpperCase()}
                          </Checkbox>
                        ))}
                      </CheckboxGroup>
                    </Stack>
                  </DropsExpandable>
                </ExpandableWrapper>

                <ExpandableWrapper marginLeft>
                  <DropsExpandable collapsible title="Marker med">
                    <HStack maxWidth="375px" wrap="wrap">
                      {VEHICLE_SET_TYPES.map(({ value, text }) => (
                        <ChoiceChip
                          key={value}
                          checked={vehicleSetTypes.includes(value)}
                          onCheckedChange={() => toggleVehicleSetType(value)}
                        >
                          {text}
                        </ChoiceChip>
                      ))}
                    </HStack>
                  </DropsExpandable>
                </ExpandableWrapper>
              </ExpandableSection>
            </ShowIfTrue>
            <DropsButton
              type="submit"
              size="sm"
              variant="primary"
              loading={postStatus === "pending"}
              ml="auto"
              data-testid="submit-button-oplogg"
              onClick={() => {
                setTimeout(() => {
                  setMessaging(false);
                }, 500); // Delay of 0.5 sec
              }}
            >
              Send
            </DropsButton>
          </FormContainer>
        </form>
        {errors.message && (
          <Text variant="xs" color="red">
            {errors.message.message}
          </Text>
        )}
      </FormProvider>
    </Grid>
  );
};
